import { v4 as uuid4 } from 'uuid'

import { BuilderDroppableElement, BuilderMenu, TableAdapter } from '@makyoapp/makyo-common'

import { IAppLayer } from '@/core/app-builder/app/layer'
import { toSdkPageBody } from '@/adapters/__project__/BuilderPageAdapter'
import { BuilderComponent, ComponentSettingKey, DEFAULT_COMPONENTS_GROUPS } from '@/shared/builder'

import { getSequentialTabIcon } from './app-builder'
import { parseCompSettings } from './comp-parsers'

type CreateElementCtx = {
  tables: TableAdapter[]
  menus: BuilderMenu[]
  layer?: IAppLayer
}

function generateFlattenedSettings(comp: BuilderComponent) {
  if (!comp) return []

  const list = [...(comp.settings.data ?? []), ...(comp.settings.design ?? [])]

  if (comp.settings.dataGroup) {
    const stgs = Object.values(comp.settings.dataGroup)
    for (const s of stgs) {
      if (Array.isArray(s.items)) {
        list.push(...s.items)
      }
    }
  }

  if (comp.settings.designGroup) {
    const stgs = Object.values(comp.settings.designGroup)
    for (const s of stgs) {
      if (Array.isArray(s.items)) {
        list.push(...s.items)
      }
    }
  }

  if (comp.settings.behavior?.custom) {
    const stgs = Object.values(comp.settings.behavior?.custom)
    for (const s of stgs) {
      if (Array.isArray(s.items)) {
        list.push(...s.items)
      }
    }
  }

  return list
}

export function createElement(compId: string, ctx: CreateElementCtx) {
  const groupId = DEFAULT_COMPONENTS_GROUPS.find((g) => g.components.find((c) => c.compId === compId))?.id
  const theComp = DEFAULT_COMPONENTS_GROUPS.flatMap((g) => g.components).find((c) => c.compId === compId)
  if (!theComp || !groupId) return [null, null] as const

  const id = uuid4()
  let newEl: BuilderDroppableElement = {
    id,
    compId: theComp.compId,
    groupId: groupId,
    title: theComp.title,
    icon: theComp.icon,
    inactive: false,
    nested: theComp.nested,
    isForm: theComp.isForm,
    isDetailPageComponent: theComp.isDetailPageComponent,
    children: [],
    parentId: null,
    parentTitle: null,
    parentType: null,
  }

  let newSettings = parseCompSettings(
    theComp,
    {
      menus: ctx.menus,
      tables: ctx.tables,
      flattened: generateFlattenedSettings(theComp),
    },
    ctx.layer?.settings._TableID,
    newEl.parentId ?? ''
  )

  return [newEl, newSettings] as const
}

export function createElementRaw(compId: string, ctx: CreateElementCtx) {
  const [newEl, newSettings] = createElement(compId, ctx)
  if (!newEl) return [null, null] as const
  return [toSdkPageBody([newEl])[0], newSettings] as const
}

type GenerateElementsProps = {
  parent: BuilderDroppableElement
  settings: Record<string, any>
  ctx: CreateElementCtx
}

export function generateTabItems({ parent, ctx }: GenerateElementsProps) {
  let elChildren: BuilderDroppableElement[] = []
  let elSettings: Record<string, any> = {}

  // generate 3 tab items containg text component
  for (let i = 0; i < 3; i++) {
    // generate tab item
    let newTabItem: BuilderDroppableElement = {
      id: uuid4(),
      compId: 'tab-item',
      groupId: 'container',
      title: `Tab`,
      customTitle: `Tab ${i + 1}`,
      icon: '',
      inactive: false,
      nested: false,
      isForm: false,
      isDetailPageComponent: false,
      children: [],
      parentId: parent.id,
      parentTitle: parent.title,
      parentType: parent.compId,
    }

    elSettings[newTabItem.id] = {
      title: newTabItem.customTitle,
      icon: getSequentialTabIcon(i),
    }

    elChildren.push(newTabItem)

    // generate children for tab item
    const [newEl, newSettings] = createElement('text', ctx)
    if (newEl) {
      newTabItem.children.push(newEl)
      newSettings[ComponentSettingKey.TextDataValue] = [
        {
          type: 'paragraph',
          children: [{ text: `Tab ${i + 1} Content` }],
        },
      ]
      elSettings[newEl.id] = newSettings
    }
  }

  return {
    children: elChildren,
    childElSettings: elSettings,
  }
}

export function generateSectionElements({ parent, ctx }: GenerateElementsProps) {
  let elChildren: BuilderDroppableElement[] = []
  let elSettings: Record<string, any> = {}

  // generate children for tab item
  const [newEl, newSettings] = createElement('text', ctx)
  if (newEl) {
    elChildren.push(newEl)
    newSettings[ComponentSettingKey.TextDataValue] = [
      {
        type: 'paragraph',
        children: [{ text: `Section Text` }],
      },
    ]
    elSettings[newEl.id] = newSettings
  }

  return {
    children: elChildren,
    childElSettings: elSettings,
  }
}
